import React, { useContext, useEffect, useState, useMemo, useRef } from "react";
import { Chart as ChartJS, LinearScale, PointElement, Tooltip, Legend, ChartOptions, Plugin } from "chart.js";
import { Scatter } from "react-chartjs-2";
import { useNavigate } from "react-router-dom";
import { MyContext } from "../../context/myContext";
import { parseDurationToDays } from "./WorkInProgress";
import { formatValue } from "../Performance Dashboard/MetricCard";

ChartJS.register(LinearScale, PointElement, Tooltip, Legend);

type ExecutionTime = {
  category: string;
  first_pass: string;
  rework: string;
};

type WorkItem = {
  state_category: string;
  execution_time: ExecutionTime[];
  title?: string;
  number?: string;
  work_item_ID?: string;
  assigned_to?: string;
  date?: string | null;
  type?: string;
  release?: string;
};

const formatDuration = (days: number): string => {
  if (days < 1) {
    const hours = Math.floor(days * 24);
    return `${hours} hour${hours !== 1 ? 's' : ''}`;
  }

  const wholeDays = Math.floor(days);
  const remainingHours = Math.floor((days - wholeDays) * 24);

  if (remainingHours === 0) {
    return `${wholeDays} day${wholeDays !== 1 ? 's' : ''}`;
  }

  return `${wholeDays} day${wholeDays !== 1 ? 's' : ''}, ${remainingHours} hour${remainingHours !== 1 ? 's' : ''}`;
};

const calculateTotalAge = (executionTimeArray: ExecutionTime[]): number => {
  if (!executionTimeArray || executionTimeArray.length === 0) return 0;

  let total = 0;
  executionTimeArray.forEach(executionTime => {
    const firstPassDuration = parseDurationToDays(executionTime.first_pass);
    const reworkDuration = parseDurationToDays(executionTime.rework);
    total += firstPassDuration + reworkDuration;
  });

  return total;
};

const hasAnyRework = (executionTimeArray: ExecutionTime[]): boolean => {
  if (!executionTimeArray || executionTimeArray.length === 0) return false;

  return executionTimeArray.some(executionTime =>
    executionTime.rework !== "00:00:00" &&
    executionTime.rework !== "00:00:00.000000"
  );
};

const POINT_STYLES = ['circle', 'rect', 'triangle', 'rectRounded', 'star'];

const getPointStyleByType = (type: string | undefined, workItemTypes: Map<string, number>): string => {
  if (!type) return POINT_STYLES[0];

  type = type.trim();

  if (workItemTypes.has(type)) {
    const styleIndex = workItemTypes.get(type)!;
    return POINT_STYLES[styleIndex];
  }

  const newIndex = workItemTypes.size % POINT_STYLES.length;
  workItemTypes.set(type, newIndex);
  return POINT_STYLES[newIndex];
};

interface CategoryTarget {
  category: string;
  target: number;
  units: string;
}

interface IReleasedetailsItems {
  state_categories: {
    datasets: CategoryTarget[]
  };
}

interface WorkItemScatterPlotProps {
  selectedStateCategory?: string;
  stateCategories: string[];
  selectedReleases?: string[];
  releaseDetails: any[];
  selectedType: string;
}

const createTargetBackgroundPlugin = (categories: string[], releaseDetails: IReleasedetailsItems[]): Plugin<'scatter'> => {
  return {
    id: 'targetBackground',
    beforeDraw: (chart: any) => {
      const { ctx, scales } = chart;
      const xScale = scales.x;
      const yScale = scales.y;

      if (!xScale || !yScale) return;

      const cumulativeTargets: { category: string; target: number; cumulativeTarget: number }[] = [];
      let cumulativeValue = 0;

      if (releaseDetails && releaseDetails.length > 0) {
        const stateCategories = releaseDetails[0].state_categories;

        const datasets = Array.isArray(stateCategories)
          ? (stateCategories[0]?.datasets || [])
          : stateCategories?.datasets || [];

        if (datasets.length > 0) {
          datasets.forEach((item: { target: string; category: string; }) => {
            const target = parseFloat(item.target);
            cumulativeValue += target;
            cumulativeTargets.push({
              category: item.category,
              target,
              cumulativeTarget: cumulativeValue
            });
          });
        }
      }
      ctx.save();

      for (let i = 0; i < categories.length; i++) {
        const category = categories[i];

        const targetInfo = cumulativeTargets.find(t => t.category === category);
        if (!targetInfo) continue;

        const rowTop = yScale.getPixelForValue(i);
        const rowBottom = yScale.getPixelForValue(i + 1);
        const rowHeight = rowBottom - rowTop;

        const targetX = xScale.getPixelForValue(targetInfo.cumulativeTarget);

        const gradient = ctx.createLinearGradient(
          xScale.left, 0,
          xScale.right, 0
        );

        const maxValue = Number(chart.scales.x.max) || 1;
        const targetPosition =  Number.isFinite(targetInfo.cumulativeTarget) ? Number(targetInfo.cumulativeTarget) / maxValue : 0;


        gradient.addColorStop(0, 'rgba(75, 192, 75, 0.15)');
        gradient.addColorStop(Math.min(targetPosition, 0.8), 'rgba(75, 192, 75, 0.15)');
        gradient.addColorStop(Math.min(targetPosition + 0.1, 0.9), 'rgba(255, 205, 86, 0.15)');
        gradient.addColorStop(1, 'rgba(255, 99, 132, 0.15)');

        ctx.fillStyle = gradient;
        ctx.fillRect(xScale.left, rowTop, xScale.right - xScale.left, rowHeight);

        ctx.beginPath();
        ctx.moveTo(targetX, rowTop);
        ctx.lineTo(targetX, rowBottom);
        ctx.strokeStyle = 'rgba(128, 128, 128, 0.5)';
        ctx.lineWidth = 1;
        ctx.stroke();

        ctx.font = '10px Arial';
        ctx.fillStyle = '#666';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillText(`Target: ${formatValue(targetInfo.cumulativeTarget)}d`, targetX, rowTop + 12);
      }

      ctx.restore();
    }
  };
};

const WorkItemScatterPlot: React.FC<WorkItemScatterPlotProps> = ({ selectedStateCategory, stateCategories, selectedReleases = [], releaseDetails, selectedType }) => {
  const [workItems, setWorkItems] = useState<WorkItem[]>([]);
  const [allWorkItems, setAllWorkItems] = useState<WorkItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [timeUnits, setTimeUnits] = useState<string>();
  const [maxAge, setMaxAge] = useState<number>(100);
  const [showFirstTimeRight, setShowFirstTimeRight] = useState<boolean>(true);
  const [showRework, setShowRework] = useState<boolean>(true);
  const [workItemTypes, setWorkItemTypes] = useState<Map<string, number>>(new Map());
  const [categoryTargets, setCategoryTargets] = useState<CategoryTarget[]>([]);

  const chartRef = useRef<any>(null);

  const { setWorkItemClicked } = useContext<any>(MyContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (releaseDetails && releaseDetails.length > 0) {
      const firstDetail = releaseDetails[0];
      if (firstDetail && firstDetail.state_categories) {
        let targets: CategoryTarget[] = [];

        if (Array.isArray(firstDetail.state_categories)) {
          if (firstDetail.state_categories.length > 0 && firstDetail.state_categories[0].datasets) {
            targets = firstDetail.state_categories[0].datasets.map((dataset: any) => ({
              category: dataset.category,
              target: parseFloat(dataset.target),
              units: dataset.units
            }));
          }
        } else if (firstDetail.state_categories.datasets) {
          targets = firstDetail.state_categories.datasets.map((dataset: any) => ({
            category: dataset.category,
            target: parseFloat(dataset.target),
            units: dataset.units
          }));
        }

        setCategoryTargets(targets);

        if (targets.length > 0 && targets[0].units) {
          setTimeUnits(targets[0].units);
        }
      }
    }
  }, [releaseDetails]);

  useEffect(() => {
    const firstDetail = releaseDetails?.[0];
    if (!firstDetail || !Array.isArray(firstDetail.releases)) return;

    setLoading(true);
    const allItems: WorkItem[] = [];
    const typeMap = new Map<string, number>();

    const units = firstDetail?.state_categories?.[0]?.datasets?.[0]?.units;
    setTimeUnits(units);

    firstDetail.releases.forEach((release: any) => {
      if (release.date === null && Array.isArray(release.work_items)) {
        release.work_items.forEach((item: any) => {
          if (item.state_category) {
            if (item.type) {
              const type = item.type.trim();
              if (!typeMap.has(type)) {
                typeMap.set(type, typeMap.size % POINT_STYLES.length);
              }
            }

            allItems.push({
              state_category: item.state_category,
              execution_time: item.execution_time,
              title: item.title,
              number: item.number,
              work_item_ID: item.work_item_ID,
              assigned_to: item.assigned_to,
              date: release.date,
              type: item.type,
              release: release.release
            });
          }
        });
      }
    });

    setWorkItemTypes(typeMap);

    const filteredItems = allItems.filter(
      (item) => item.execution_time !== null && item.state_category !== null
    );

    setAllWorkItems(filteredItems);
    setWorkItems(filteredItems);

    if (filteredItems.length > 0) {
      const itemAges = filteredItems.map(item =>
        calculateTotalAge(item.execution_time)
      );
      const calculatedMaxAge = Math.max(...itemAges);
      setMaxAge(Math.ceil(calculatedMaxAge / 10) * 10);
    }
    setLoading(false);
  }, [releaseDetails]);

  useEffect(() => {
    if (selectedReleases && selectedReleases.length > 0) {
      const filteredItems = allWorkItems.filter(item =>
        selectedReleases.includes(item.release as string)
      );
      setWorkItems(filteredItems);

      if (filteredItems.length > 0) {
        const itemAges = filteredItems.map(item => calculateTotalAge(item.execution_time));
        const calculatedMaxAge = Math.max(...itemAges);
        const nextNearestTen = Math.ceil(calculatedMaxAge / 10) * 10;
        setMaxAge(nextNearestTen);
      }
    } else {
      setWorkItems(allWorkItems);

      if (allWorkItems.length > 0) {
        const itemAges = allWorkItems.map(item => calculateTotalAge(item.execution_time));
        const calculatedMaxAge = Math.max(...itemAges);
        const nextNearestTen = Math.ceil(calculatedMaxAge / 10) * 10;
        setMaxAge(nextNearestTen);
      }
    }
  }, [selectedReleases, allWorkItems]);

  const scatterData = useMemo(() => {
    const filteredItems = workItems.filter(item => {
      const itemHasRework = hasAnyRework(item.execution_time);
      if (itemHasRework && !showRework) return false;
      if (!itemHasRework && !showFirstTimeRight) return false;
      return true;
    });

    let stageFilteredCategories = [...stateCategories];
    let stageFilteredItems = [...filteredItems];

    if (selectedStateCategory && stateCategories.includes(selectedStateCategory)) {
      const stageIndex = stateCategories.indexOf(selectedStateCategory);
      stageFilteredCategories = stateCategories.slice(stageIndex);
      stageFilteredItems = filteredItems.filter(item => {
        const itemStageIndex = stateCategories.indexOf(item.state_category);
        return itemStageIndex >= stageIndex;
      });
    }

    const groupMap: { [key: string]: WorkItem[] } = {};
    stageFilteredItems.forEach(item => {
      const age = Math.round(calculateTotalAge(item.execution_time));
      const key = `${age}-${item.state_category}`;
      if (!groupMap[key]) groupMap[key] = [];
      groupMap[key].push(item);
    });

    const firstTimeRightItems: any[] = [];
    const reworkItems: any[] = [];

    stageFilteredCategories.forEach((category, categoryIndex) => {
      const categoryItems = stageFilteredItems.filter(
        (item) => item.state_category === category
      );

      categoryItems.forEach(item => {
        const age = calculateTotalAge(item.execution_time);
        const itemHasRework = hasAnyRework(item.execution_time);

        const key = `${Math.round(age)}-${category}`
        let groupOffset = 0;
        if (groupMap[key]) {
          groupOffset = groupMap[key].indexOf(item) / groupMap[key].length;
        }
        const yPosition = categoryIndex + 0.1 + groupOffset;

        const pointStyle = getPointStyleByType(item.type, workItemTypes);

        const dataPoint = {
          x: age,
          y: yPosition,
          tooltipInfo: item,
          actualAge: age,
          hasRework: itemHasRework,
          pointStyle: pointStyle,
          category: categoryIndex
        };

        if (itemHasRework) {
          reworkItems.push(dataPoint);
        } else {
          firstTimeRightItems.push(dataPoint);
        }
      });
    });

    return {
      datasets: [
        {
          data: firstTimeRightItems,
          backgroundColor: "rgba(54, 162, 235, 0.7)",
          borderColor: "rgba(54, 162, 235, 1)",
          pointRadius: 6,
          pointHoverRadius: 9,
          pointStyle: (ctx: any) => {
            if (!ctx.raw) return "circle";
            return ctx.raw.pointStyle;
          },
          showLine: false,
          hidden: !showFirstTimeRight
        },
        {
          data: reworkItems,
          backgroundColor: "rgba(255, 206, 86, 0.8)",
          borderColor: "rgba(255, 153, 0, 1)",
          pointRadius: 6,
          pointHoverRadius: 9,
          pointStyle: (ctx: any) => {
            if (!ctx.raw) return "circle";
            return ctx.raw.pointStyle;
          },
          showLine: false,
          hidden: !showRework
        }
      ]
    };
  }, [workItems, stateCategories, showFirstTimeRight, showRework, selectedStateCategory, workItemTypes]);

  const visibleStageCategories = useMemo(() => {
    if (selectedStateCategory && stateCategories.includes(selectedStateCategory)) {
      const stageIndex = stateCategories.indexOf(selectedStateCategory);
      return stateCategories.slice(stageIndex);
    }
    return stateCategories;
  }, [stateCategories, selectedStateCategory]);

  const targetBackgroundPlugin = useMemo(() => {
    return createTargetBackgroundPlugin(visibleStageCategories, releaseDetails);
  }, [visibleStageCategories, releaseDetails]);

  useEffect(() => {
    const existingBgPlugin = ChartJS.registry.plugins.get('targetBackground');
    if (existingBgPlugin) {
      ChartJS.unregister(existingBgPlugin);
    }

    ChartJS.register(targetBackgroundPlugin);

    return () => {
      const bgPlugin = ChartJS.registry.plugins.get('targetBackground');
      if (bgPlugin) {
        ChartJS.unregister(bgPlugin);
      }
    };
  }, [targetBackgroundPlugin]);

  const getCumulativeTargetForCategory = (category: string): number => {
    let cumulativeTarget = 0;
    const categoryIndex = visibleStageCategories.indexOf(category);

    if (categoryIndex === -1) return 0;

    for (let i = 0; i <= categoryIndex; i++) {
      const currCategory = visibleStageCategories[i];
      const targetInfo = categoryTargets.find(t => t.category === currCategory);
      if (targetInfo) {
        cumulativeTarget += targetInfo.target;
      }
    }

    return cumulativeTarget;
  };

  const chartOptions = useMemo((): ChartOptions<"scatter"> => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      aspectRatio: 2,
      layout: {
        padding: {
          left: 10,
          right: 10,
          top: 10,
          bottom: 5
        }
      },
      scales: {
        x: {
          type: "linear",
          title: {
            display: true,
            text: `Age (in ${timeUnits})`,
            font: {
              size: 12,
              weight: 'bold'
            },
            padding: 5
          },
          min: 0,
          max: maxAge,
          grid: {
            color: "rgba(0, 0, 0, 0.1)",
            display: false,
          },
          ticks: {
            font: {
              size: 12
            },
            stepSize: 10
          }
        },

        y: {
          type: "linear",
          min: 0,
          max: visibleStageCategories.length,
          position: 'left',
          grid: {
            color: (context) => {
              const index = context.tick.value;
              if (index === 0 || !Number.isInteger(index)) {
                return "rgba(0, 0, 0, 0)";
              }
              return "rgba(0, 0, 0, 0.1)";
            },
            z: 0,
            drawTicks: false
          },
          ticks: {
            callback: function (value: any) {
              const numValue = Number(value);
              const isHalfValue = Math.abs(numValue - Math.floor(numValue) - 0.5) < 0.001;

              if (isHalfValue) {
                const categoryIndex = Math.floor(numValue);
                if (categoryIndex >= 0 && categoryIndex < visibleStageCategories.length) {
                  return visibleStageCategories[categoryIndex];
                }
              }
              return "";
            },
            stepSize: 0.5,
            z: 1,
            font: {
              size: 12,
              weight: 'bold'
            },
            padding: 8,
            align: 'center',
            crossAlign: 'center',
          } as any,
          afterFit: function (scaleInstance: any) {
            scaleInstance.width = 120;
          },
        },
      },
      interaction: {
        mode: 'point',
        intersect: true
      },
      plugins: {
        tooltip: {
          mode: 'nearest',
          intersect: true,
          backgroundColor: "rgba(0, 0, 0, 0.85)",
          titleFont: {
            weight: "bold",
            size: 14
          },
          bodyFont: {
            size: 12
          },
          padding: 12,
          cornerRadius: 6,
          displayColors: false,
          callbacks: {
            title: function (tooltipItems) {
              const dataPoint = tooltipItems[0].raw as any;
              const item = dataPoint.tooltipInfo;

              return `${item.type} ${item.work_item_ID}`;
            },
            label: function (tooltipItem) {
              const dataPoint = tooltipItem.raw as any;
              const item = dataPoint.tooltipInfo;
              const actualAge = dataPoint.actualAge;
              const hasRework = dataPoint.hasRework;

              const cumulativeTarget = getCumulativeTargetForCategory(item.state_category);

              const targetStatus = actualAge <= cumulativeTarget ?
                `✅ On Target (${formatValue(cumulativeTarget)} ${timeUnits})` :
                `⚠️ Exceeds Target (${formatValue(cumulativeTarget)} ${timeUnits})`;

              return [
                `Age: ${formatDuration(actualAge)}`,
                `Stage: ${item.state_category || "Unknown"}`,
                `Status: ${targetStatus}`,
                `Rework: ${hasRework ? "Yes" : "No"}`,
              ].filter(Boolean);
            },
          },
        },
        legend: {
          display: false,
        },
      },
      elements: {
        line: {
          tension: 0,
          borderWidth: 0,
          fill: false,
          cubicInterpolationMode: 'monotone'
        },
        point: {
          radius: 6,
          hoverRadius: 9
        }
      },
      onClick: (_, elements) => {
        if (elements.length === 0) return;

        const element = elements[0];
        const dataPoint = scatterData.datasets[element.datasetIndex].data[element.index] as any;
        const item = dataPoint.tooltipInfo;

        if (item?.work_item_ID) {
          navigate(`/work-item?viewMode=WorkItem&workItemId=${item.work_item_ID}`);
          setWorkItemClicked(true);
        }
      },
    };
  }, [visibleStageCategories, timeUnits, maxAge, showFirstTimeRight, showRework, scatterData, navigate, categoryTargets]);

  const toggleFirstTimeRight = () => setShowFirstTimeRight(!showFirstTimeRight);
  const toggleRework = () => setShowRework(!showRework);

  const renderWorkItemTypeLegend = () => {
    const typeEntries = Array.from(workItemTypes.entries());

    return (
      <div className="flex items-center gap-3 text-xs mr-6">
        {typeEntries.map(([type, styleIndex]) => {
          const style = POINT_STYLES[styleIndex];
          let shapeElement;

          switch (style) {
            case 'rect':
              shapeElement = <div style={{
                width: '10px',
                height: '10px',
                backgroundColor: '#a8a8a8',
                marginRight: '4px'
              }}></div>;
              break;
            case 'triangle':
              shapeElement = <div style={{
                width: '0',
                height: '0',
                borderLeft: '5px solid transparent',
                borderRight: '5px solid transparent',
                borderBottom: '10px solid #a8a8a8',
                marginRight: '4px'
              }}></div>;
              break;
            case 'rectRounded':
              shapeElement = <div style={{
                width: '10px',
                height: '10px',
                borderRadius: '2px',
                backgroundColor: '#a8a8a8',
                marginRight: '4px'
              }}></div>;
              break;
            case 'star':
              shapeElement = <div style={{
                width: '10px',
                height: '10px',
                clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
                backgroundColor: '#a8a8a8',
                marginRight: '4px'
              }}></div>;
              break;
            case 'circle':
            default:
              shapeElement = <div style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: '#a8a8a8',
                marginRight: '4px'
              }}></div>;
              break;
          }

          return (
            <div key={type} className="flex items-center">
              {shapeElement}
              <span>{type}</span>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return <div className="w-full h-[500px] flex items-center justify-center">Loading work item data...</div>;
  }

  return (
    <div className="w-full p-4 bg-white rounded-lg shadow">
      <div className="flex justify-between items-center mb-2">
        <div className="w-full">
          <h2 className="card-title">Progress Details by Work Item</h2>
        </div>
        <div className="flex items-center gap-4 whitespace-nowrap">
          <div className="flex items-center text-xs mr-4">
            <div style={{
              background: 'linear-gradient(to right, rgba(75, 192, 75, 0.3), rgba(255, 205, 86, 0.3), rgba(255, 99, 132, 0.3))',
              width: '100px',
              height: '10px',
              borderRadius: '2px'
            }}></div>
            <div className="ml-2">On target → Past target</div>
          </div>

          {renderWorkItemTypeLegend()}

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={showFirstTimeRight}
                onChange={toggleFirstTimeRight}
                className="appearance-none w-4 h-4 rounded border border-gray-300 bg-white checked:bg-blue-500 checked:border-blue-500 cursor-pointer"
              />
              <span className="checkbox-custom absolute pointer-events-none w-4 h-4" />
              <span className="whitespace-nowrap">First Time Right</span>
            </label>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={showRework}
                onChange={toggleRework}
                className="appearance-none w-4 h-4 rounded border border-gray-300 checked:bg-yellow-500 checked:border-yellow-500 cursor-pointer"
              />
              <span className="checkbox-custom absolute pointer-events-none w-4 h-4" />
              <span className="whitespace-nowrap">Rework</span>
            </label>
          </div>
        </div>
      </div>

      <div className="h-[450px]" style={{ padding: '10px' }}>
        <Scatter
          ref={chartRef}
          data={scatterData}
          options={chartOptions}
        />
      </div>
    </div>
  );
};

export default WorkItemScatterPlot;
